import { Link } from "react-router-dom";
import { Heart, MessageCircle, Loader2 } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { useMatches } from "../hooks/useDating";

function formatTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return "gerade eben";
  if (diffMin < 60) return `vor ${diffMin} Min.`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `vor ${diffH} Std.`;
  if (diffH < 48) return "gestern";
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

export function MatchesInbox() {
  const { data: matches = [], isLoading } = useMatches();

  return (
    <AppShell>
      <section className="container max-w-2xl pt-6">
        <h1 className="text-2xl font-bold tracking-tight">Matches</h1>
        <p className="mt-1 text-sm text-ink-500">
          Alle, die auch dich gut finden.
        </p>

        {isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
          </div>
        ) : matches.length === 0 ? (
          <div className="card mt-8 flex flex-col items-center p-10 text-center">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-3xl bg-brand-50 text-brand-500">
              <Heart className="h-7 w-7" fill="currentColor" />
            </span>
            <h2 className="mt-5 text-lg font-bold tracking-tight">
              Noch keine Matches
            </h2>
            <p className="mt-1 max-w-xs text-sm text-ink-500">
              Finde Menschen im Deck. Wenn ihr euch gegenseitig mögt, landet ihr
              hier.
            </p>
            <Link to="/deck" className="btn-primary mt-6">
              Jetzt entdecken
            </Link>
          </div>
        ) : (
          <ul className="mt-6 space-y-2">
            {matches.map((m) => (
              <li key={m.id}>
                <Link
                  to={`/matches/${m.id}`}
                  className="flex items-center gap-4 rounded-3xl bg-white p-4 shadow-soft transition hover:shadow-card"
                >
                  <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-full bg-brand-gradient">
                    {m.partnerPhotoUrl ? (
                      <img
                        src={m.partnerPhotoUrl}
                        alt={m.partnerName}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <Heart className="h-6 w-6 text-white" fill="currentColor" />
                      </span>
                    )}
                    {m.unread > 0 && (
                      <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-500 px-1.5 text-[10px] font-bold text-white">
                        {m.unread}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="truncate text-base font-bold text-ink-900">
                        {m.partnerName}
                      </h3>
                      <span className="shrink-0 text-xs text-ink-500">
                        {formatTime(m.lastMsgAt)}
                      </span>
                    </div>
                    <p className="truncate text-sm text-ink-500">
                      {m.lastMsgPreview ?? "Sag Hallo!"}
                    </p>
                  </div>
                  <MessageCircle className="h-5 w-5 text-ink-300" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </AppShell>
  );
}
